import { getInstance } from './instance';

const instance = getInstance();

export const getModAssets = () => {
  return instance
    .get('mod/assets')
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

/**
 * The member's distinct GHL tags for the "Contacts with tags" audience mode.
 * Returns `{ tags: string[] }` (sorted/de-duped server-side), 422 when the
 * member has no Marketing Hub location, or 502 on a GHL read failure.
 */
export const getModAudienceTags = () => {
  return instance
    .get('mod/audience/tags')
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

export const getModAudiencePreview = (mode, tags = []) => {
  const params = { mode };

  if (mode === 'tags' && tags.length) {
    params.tags = tags.join(',');
  }

  return instance
    .get('mod/audience/preview', { params })
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

export const createModOrder = (payload) => {
  return instance
    .post('mod/orders', payload)
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

export const submitModOrderAudience = (orderId) => {
  return instance
    .post(`mod/orders/${orderId}/submit-audience`)
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

export const getModOrder = (orderId) => {
  return instance
    .get(`mod/orders/${orderId}`)
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

export const getModOrders = () => {
  return instance
    .get('mod/orders')
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

/**
 * DS pagination is 0-indexed (`skip = page * limit`), while UI pages are
 * 1-based. All paginated calls in this module take the UI's 1-based page and
 * translate at this boundary — pass `page = 1` for the first page.
 */
const toDsPage = (page) => Math.max(0, Number(page || 1) - 1);

export const getModOrderRecipients = (
  orderId,
  { skipped, page = 1, limit = 25, search } = {},
) => {
  // The DS recipients endpoint honors an arbitrary `limit`; cap at 100 so the
  // page-size selector (task 47) can never request an unbounded page.
  const params = { page: toDsPage(page), limit: Math.min(Number(limit) || 25, 100) };

  if (skipped !== undefined) {
    params.skipped = skipped;
  }
  if (search) {
    params.search = search;
  }

  return instance
    .get(`mod/orders/${orderId}/recipients`, { params })
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

/**
 * Recipient curation (ADR-006): toggle selection and/or override the mailing
 * address for one recipient. Returns `{ order, recipient }` — the server
 * recounts totals, so callers update from `order`, never client math.
 */
export const updateModOrderRecipient = (orderId, recipientId, patch) => {
  return instance
    .patch(`mod/orders/${orderId}/recipients/${recipientId}`, patch)
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

/** Bulk select-all / select-none over the order's mailable recipients. */
export const setModOrderRecipientsSelection = (orderId, selected) => {
  return instance
    .post(`mod/orders/${orderId}/recipients/select-all`, { selected })
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

export const payModOrder = (orderId) => {
  return instance
    .post(`mod/orders/${orderId}/pay`)
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

export const cancelModOrder = (orderId) => {
  return instance
    .post(`mod/orders/${orderId}/cancel`)
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

export const getPrintCreditsBalance = () => {
  return instance
    .get('credits/balance', { params: { creditType: 'print' } })
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};

export const getPrintCreditsHistory = (page = 1, limit = 10) => {
  return instance
    .get('credits/history', { params: { page: toDsPage(page), limit } })
    .then((res) => res.data)
    .catch((error) => Promise.reject(error));
};
